import React, {  } from 'react';
import type { FC } from 'react';
import { Box, Chip, Divider, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress'
import SearchIcon from '@/icons/Search';
import LockIcon from '@mui/icons-material/Lock';
import { useAmbientFacilitiesContext } from './search/AmbientFacilitiesContext';
import DataCatalogGeneralSearch from './search/DataCatalogGeneralSearch';
import { useDataCatalogContext } from './dataCatalogContext/DataCatalogContext';
import { EncryptMappingStatus, SecondaryClassificationResponseDto } from '@/gen/api/governance';
import PopAutocompleteFilter from '../input/PopAutocompleteFilter';
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { ReduxState } from 'ra-core';
import { useSelector } from 'react-redux';
import { Criteria } from './dataCatalogContext/types';



export const DataCatalogFilters: FC = (props) => {
  const { criterias, criteriaLockedDown, pushCriteria, removeCriteria } = useDataCatalogContext()

  const { classifications } = useAmbientFacilitiesContext()
  const loading = useSelector<ReduxState>(state => state.admin.loading > 0)

  return (
    <div >
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          p: 2
        }}
      >
        {loading ? 
          <CircularProgress size={20} /> :
          <SearchIcon fontSize="small" />}
        <Box
          sx={{
            flexGrow: 1,
            ml: 3
          }}
        >
          <DataCatalogGeneralSearch />
        </Box>
      </Box>
      <Divider />
      {
        criterias.length > 0
          ? (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                p: 2
              }}
            >
              {criteriaLockedDown && <LockIcon sx={{ width: 12 }} />}
              {criterias.sort((a, b) => a.kind < b.kind ? -1 : 1).map((filterItem, i, arr) => (
                <React.Fragment key={i}>
                  {i > 0 && arr[i - 1].kind !== filterItem.kind && 
                    <Typography variant="caption" color="InactiveCaptionText" sx={{ mx: 0.75 }} >- and -</Typography>
                  }
                  <Chip
                    key={i}
                    label={(
                      <Box
                        sx={{
                          alignItems: 'center',
                          display: 'flex',
                          '& span': {
                            fontWeight: 600
                          }
                        }}
                      >
                    <span>
                      {getCriteriaLabel(filterItem)}
                    </span>
                        :
                        {' '}
                        {getCriteriaDisplayValue(filterItem)}
                      </Box>
                    )}
                    onDelete={(): void => removeCriteria(filterItem)}
                    sx={{ m: 1 }}
                    variant="outlined"
                  />
                </React.Fragment>
              ))}
            </Box>
          )
          : (
            <Box sx={{ p: 3, display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              {criteriaLockedDown && <LockIcon sx={{ width: 12, pr: 2 }} />}
              <Typography
                component="span"
                color="InactiveCaptionText"
                variant="subtitle2"
              >
                No filters applied
              </Typography>
            </Box>
          )
      }
      <Divider />
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          p: 1
        }}
      >
        <PopAutocompleteFilter
          label="Classification"
          AutocompleteProps={{
            id: 'classification-picker',
            disableCloseOnSelect: true,
            options: classifications,
            getOptionLabel: (option) => option.text,
            renderOption: (props, option, { inputValue }) => {
              const matches = match(option.text, inputValue, {insideWords:true});
              const parts = parse(option.text, matches);

              return (
                <li {...props}>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', wordWrap: 'normal', whiteSpace: 'nowrap' }}>
                    {parts.map((part, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: part.highlight ? 700 : 400,
                        }}
                      >
                        {part.text}
                      </span>
                    ))}
                  </Box>
                </li>
              );
            },
            onChange: (event, option) => {
              const clsItem = option as SecondaryClassificationResponseDto
              if (clsItem) {
                pushCriteria({
                  kind: 'Classification',
                  primaryId: clsItem.primaryClassification,
                  secondaryId: clsItem.secondaryClassificationId,
                  primaryName: clsItem.primaryClassificationText || '',
                  secondaryName: clsItem.name || '',
                })
              }
            },
          }}            
        />
      </Box>
    </div>
  );
};


const getCriteriaLabel = (x: Criteria) => {
  switch (x.kind) {
    case 'Tag': { return 'Tag' }
    case 'At':  { return 'Table' }
    case 'FieldFuzzy': { return 'Field' }
    case 'Path': { return 'Path' }
    case 'Classification': { return 'Classification' }
    case 'EncryptStatus':  { return 'Encryption Status' }
  }
}

const getCriteriaDisplayValue = (x: Criteria) => {
  switch (x.kind) {
    case 'Tag': { return x.tag }
    case 'At':  { return `like "*${x.at}*"` }
    case 'FieldFuzzy': { 
      return `contains ${x.fields.length > 1 ? 'any of ' : ''}${
        x.fields.map(s => `"${s}"`).join(', ')}` 
    }
    case 'Path': { return x.text.replace(/\.*$/, '') }
    case 'Classification': { 
      return [x.primaryName, x.secondaryName].filter(s => s).join(' - ') 
    }
    case 'EncryptStatus': { 
      switch (x.encryptStatus) {
        case EncryptMappingStatus.Mapped:        return 'Mapped';
        case EncryptMappingStatus.Skipped:       return 'Skipped';
        case EncryptMappingStatus.PendingReview: return 'Pending Review';
      }
    }
  }
}

